import Link from "next/link";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

type Variant = "gold-filled" | "gold-outline";
type Size = "md" | "sm";

const baseClasses =
  "inline-block rounded-sm font-body text-sm uppercase tracking-wider transition-colors";

const sizeClasses: Record<Size, string> = {
  md: "px-8 py-3",
  sm: "px-5 py-2",
};

const variantClasses: Record<Variant, string> = {
  "gold-filled": "bg-gold text-black hover:bg-gold-bright",
  "gold-outline":
    "border border-gold text-gold hover:bg-gold hover:text-black",
};

type CommonProps = {
  variant: Variant;
  size?: Size;
  className?: string;
  children: ReactNode;
};

type AsLink = CommonProps & {
  href: string;
  type?: never;
  onClick?: never;
  disabled?: never;
};

type AsButton = CommonProps & {
  href?: never;
} & Pick<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "onClick" | "disabled" | "aria-label">;

export type ButtonProps = AsLink | AsButton;

export function Button(props: ButtonProps) {
  const { variant, size = "md", className, children } = props;
  const classes = cn(
    baseClasses,
    sizeClasses[size],
    variantClasses[variant],
    className,
  );

  if ("href" in props && props.href !== undefined) {
    return (
      <Link href={props.href} className={classes}>
        {children}
      </Link>
    );
  }

  const { type = "button", onClick, disabled } = props as AsButton;
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={classes}>
      {children}
    </button>
  );
}
